import React, { useState, useRef, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@mellify/ui';
import {
  Upload,
  X,
  AlertCircle,
  CheckCircle2,
  Loader2,
  CloudUpload,
} from 'lucide-react';
import { useUploadDocuments } from '../hooks/useUploadDocuments';
import { useExistingTags } from '../hooks/useExistingTags';
import { TagInput } from './TagInput';
import type { UploadFileItem } from '../hooks/useUploadDocuments';

interface UploadDocumentsModalProps {
  open: boolean;
  onClose: () => void;
}

const ACCEPTED_EXTENSIONS = '.pdf,.docx,.pptx,.csv,.txt,.xlsx,.md,.html,.json';

const FILE_TYPE_ICONS: Record<string, string> = {
  pdf: '📄',
  docx: '📝',
  pptx: '📊',
  csv: '📋',
  txt: '📃',
  xlsx: '📈',
  md: '📑',
  html: '🌐',
  json: '🔧',
};

function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(1)} KB`;
  return `${bytes} B`;
}

export const UploadDocumentsModal: React.FC<UploadDocumentsModalProps> = ({
  open,
  onClose,
}) => {
  const {
    files,
    addFiles,
    removeFile,
    updateFile,
    uploadAll,
    clearCompleted,
    reset,
    isUploading,
    allComplete,
  } = useUploadDocuments();

  const { tags: existingTags } = useExistingTags();
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        addFiles(droppedFiles);
      }
    },
    [addFiles]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files ? Array.from(e.target.files) : [];
      if (selected.length > 0) {
        addFiles(selected);
      }
      // Reset input so same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [addFiles]
  );

  const handleClose = useCallback(() => {
    if (isUploading) return;
    reset();
    onClose();
  }, [isUploading, reset, onClose]);

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const successCount = files.filter((f) => f.status === 'success').length;

  // Collect all tags from files + existing for autocomplete
  const allTagSuggestions = Array.from(
    new Set([...existingTags, ...files.flatMap((f) => f.tags)])
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="lg"
      closeOnOverlayClick={!isUploading}
      closeOnEscape={!isUploading}
      aria-label="Upload documents"
    >
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-900/20">
            <Upload className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-900 dark:text-dark-100">
              Upload Documents
            </h2>
            <p className="text-xs text-dark-500 dark:text-dark-400 mt-0.5">
              Add documents to your knowledge base for AI training
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="space-y-5">
        {/* Drag & Drop Zone */}
        {files.length < 10 && (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`
              relative flex flex-col items-center justify-center py-10 px-6 rounded-xl border-2 border-dashed cursor-pointer transition-all
              ${
                isDragOver
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                  : 'border-dark-300 dark:border-dark-600 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-dark-50 dark:hover:bg-dark-800/50'
              }
              ${isUploading ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            <CloudUpload
              className={`w-10 h-10 mb-3 transition-colors ${
                isDragOver
                  ? 'text-primary-500'
                  : 'text-dark-400 dark:text-dark-500'
              }`}
            />
            <p className="text-sm font-medium text-dark-700 dark:text-dark-200 mb-1">
              {isDragOver ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-xs text-dark-500 dark:text-dark-400 mb-3">
              or click to browse
            </p>
            <p className="text-xs text-dark-400 dark:text-dark-500">
              PDF, DOCX, PPTX, CSV, TXT, XLSX, MD, HTML, JSON — Max 50MB per
              file, up to 10 files
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_EXTENSIONS}
              onChange={handleFileSelect}
              className="hidden"
              disabled={isUploading}
            />
          </div>
        )}

        {/* File List */}
        {files.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-dark-700 dark:text-dark-200">
                Files ({files.length}/10)
              </h3>
              {successCount > 0 && !isUploading && (
                <button
                  type="button"
                  onClick={clearCompleted}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Clear completed
                </button>
              )}
            </div>

            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
              {files.map((fileItem) => (
                <UploadFileRow
                  key={fileItem.id}
                  fileItem={fileItem}
                  onRemove={() => removeFile(fileItem.id)}
                  onUpdate={(updates) => updateFile(fileItem.id, updates)}
                  tagSuggestions={allTagSuggestions}
                  isUploading={isUploading}
                />
              ))}
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button
          variant="outline"
          size="md"
          onClick={handleClose}
          disabled={isUploading}
        >
          {allComplete ? 'Done' : 'Cancel'}
        </Button>
        {!allComplete && (
          <Button
            variant="primary"
            size="md"
            leftIcon={
              isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )
            }
            onClick={uploadAll}
            disabled={isUploading || pendingCount === 0}
            loading={isUploading}
          >
            {isUploading
              ? 'Uploading...'
              : `Upload All (${pendingCount})`}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

UploadDocumentsModal.displayName = 'UploadDocumentsModal';

// ─── File Row Component ─────────────────────────────────────────────────────

interface UploadFileRowProps {
  fileItem: UploadFileItem;
  onRemove: () => void;
  onUpdate: (updates: Partial<Pick<UploadFileItem, 'title' | 'description' | 'tags'>>) => void;
  tagSuggestions: string[];
  isUploading: boolean;
}

const UploadFileRow: React.FC<UploadFileRowProps> = ({
  fileItem,
  onRemove,
  onUpdate,
  tagSuggestions,
  isUploading,
}) => {
  const ext = getFileExtension(fileItem.file.name);
  const icon = FILE_TYPE_ICONS[ext] || '📄';
  const isEditable = fileItem.status === 'pending' && !isUploading;

  return (
    <div
      className={`
        rounded-lg border p-4 transition-colors
        ${
          fileItem.status === 'error'
            ? 'border-danger-300 dark:border-danger-700 bg-danger-50/50 dark:bg-danger-900/10'
            : fileItem.status === 'success'
              ? 'border-success-300 dark:border-success-700 bg-success-50/50 dark:bg-success-900/10'
              : 'border-dark-200 dark:border-dark-700 bg-white dark:bg-dark-800'
        }
      `}
    >
      {/* Header row: icon, filename, size, status, remove */}
      <div className="flex items-start gap-3 mb-3">
        <span className="text-xl leading-none mt-0.5" aria-hidden="true">
          {icon}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-dark-900 dark:text-dark-100 truncate">
            {fileItem.file.name}
          </p>
          <p className="text-xs text-dark-500 dark:text-dark-400">
            {formatFileSize(fileItem.file.size)} · {ext.toUpperCase()}
          </p>
        </div>

        {/* Status indicator */}
        <div className="flex items-center gap-2 shrink-0">
          {fileItem.status === 'uploading' && (
            <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />
          )}
          {fileItem.status === 'success' && (
            <CheckCircle2 className="w-4 h-4 text-success-500" />
          )}
          {fileItem.status === 'error' && (
            <AlertCircle className="w-4 h-4 text-danger-500" />
          )}

          {/* Remove button */}
          {(fileItem.status === 'pending' || fileItem.status === 'error') && !isUploading && (
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex items-center justify-center w-6 h-6 rounded-md text-dark-400 hover:text-danger-500 hover:bg-dark-100 dark:hover:bg-dark-700 transition-colors"
              aria-label={`Remove ${fileItem.file.name}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Error message */}
      {fileItem.status === 'error' && fileItem.errorMessage && (
        <p className="text-xs text-danger-600 dark:text-danger-400 mb-3 flex items-center gap-1.5">
          <AlertCircle className="w-3 h-3 shrink-0" />
          {fileItem.errorMessage}
        </p>
      )}

      {/* Progress bar */}
      {fileItem.status === 'uploading' && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-dark-500 dark:text-dark-400">Uploading...</span>
            <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
              {fileItem.progress}%
            </span>
          </div>
          <div className="h-1.5 bg-dark-200 dark:bg-dark-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${fileItem.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Editable fields (only when pending) */}
      {(fileItem.status === 'pending' || fileItem.status === 'error') && (
        <div className="space-y-2.5">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-dark-600 dark:text-dark-300 mb-1">
              Title
            </label>
            <input
              type="text"
              value={fileItem.title}
              onChange={(e) => onUpdate({ title: e.target.value })}
              disabled={!isEditable}
              placeholder="Document title"
              className="block w-full px-3 py-1.5 text-xs rounded-md border border-dark-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-dark-900 dark:text-dark-100 placeholder:text-dark-400 dark:placeholder:text-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-dark-600 dark:text-dark-300 mb-1">
              Description <span className="text-dark-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={fileItem.description}
              onChange={(e) => onUpdate({ description: e.target.value })}
              disabled={!isEditable}
              placeholder="Brief description of this document"
              className="block w-full px-3 py-1.5 text-xs rounded-md border border-dark-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-dark-900 dark:text-dark-100 placeholder:text-dark-400 dark:placeholder:text-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-dark-600 dark:text-dark-300 mb-1">
              Tags <span className="text-dark-400 font-normal">(optional)</span>
            </label>
            <TagInput
              value={fileItem.tags}
              onChange={(tags) => onUpdate({ tags })}
              suggestions={tagSuggestions}
              placeholder="Add tags..."
              disabled={!isEditable}
            />
          </div>
        </div>
      )}

      {/* Success message */}
      {fileItem.status === 'success' && (
        <p className="text-xs text-success-600 dark:text-success-400 flex items-center gap-1.5">
          <CheckCircle2 className="w-3 h-3 shrink-0" />
          Uploaded successfully — processing will begin shortly
        </p>
      )}
    </div>
  );
};